import React, { useEffect, useState, useRef } from 'react';
import { TaskDefinitionSummary } from '../types';
import { X, Save } from 'lucide-react';
import styles from './TemplateControls.module.css';
import { api } from '../services/api';
import PDFViewer from './PDFViewer';

function base64ToBlob(base64: string, mime = 'application/pdf') {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mime });
}

interface TemplateSettingsModalProps {
  isVisible: boolean;
  onClose: () => void;
  template: TaskDefinitionSummary;
}

interface PdfFieldMeta {
  name: string;
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

export const TemplateSettingsModal: React.FC<TemplateSettingsModalProps> = ({ isVisible, onClose, template }) => {
  const [pdfFields, setPdfFields] = useState<PdfFieldMeta[]>([]);
  const [renameMap, setRenameMap] = useState<Record<string, string>>({});
  const [defaultValueMap, setDefaultValueMap] = useState<Record<string, string>>({});
  const [selectedField, setSelectedField] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const leftPanelRef = useRef<HTMLDivElement>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  // Extract S3 key from template
  const s3Key = template.pdfTemplateS3Key || '';
  
  // Fetch PDF from backend and create object URL
  useEffect(() => {
    if (!s3Key) return;
    (async () => {
      try {
        const base64 = await api.fetchPdfBase64(s3Key);
        const blob = base64ToBlob(base64);
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
      } catch (err) {
        setPdfUrl(null);
      }
    })();
  }, [s3Key]);

  // Remove manual canvas rendering and use PDFViewer

  // Scroll to field input when selected
  useEffect(() => {
    if (selectedField && leftPanelRef.current) {
      // Find the element by iterating through children instead of using querySelector
      const fieldElements = leftPanelRef.current.querySelectorAll('[data-field]');
      for (const el of fieldElements) {
        if (el.getAttribute('data-field') === selectedField) {
          (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
          break;
        }
      }
    }
  }, [selectedField]);

  const handleSave = async () => {
    if (!s3Key) {
      alert('No S3 key found for template');
      return;
    }

    // Filter out empty rename mappings
    const filteredRenameMap = Object.fromEntries(
      Object.entries(renameMap).filter(([_, newName]) => newName.trim() !== '')
    );

    // Filter out empty default value mappings
    const filteredDefaultValueMap = Object.fromEntries(
      Object.entries(defaultValueMap).filter(([_, defaultValue]) => defaultValue.trim() !== '')
    );

    if (Object.keys(filteredRenameMap).length === 0 && Object.keys(filteredDefaultValueMap).length === 0) {
      alert('No field renames or default values to save');
      return;
    }

    setIsSaving(true);
    try {
      // Save field renames if any
      if (Object.keys(filteredRenameMap).length > 0) {
       await api.renameTemplateFields(s3Key, filteredRenameMap, template.id);
      }

      // Save default values if any
      if (Object.keys(filteredDefaultValueMap).length > 0) {
        await api.saveDefaultValues(template.id, filteredDefaultValueMap);
      }

      alert('Template settings saved successfully!');
      onClose();
    } catch (error) {
      console.error('Error saving template settings:', error);
      alert('Error saving template settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className={styles.fullPageModalOverlay}>
      <div className={styles.fullPageModalContent}>
        <header className={styles.fullPageModalHeader}>
          <h2>Template Settings: {template.name}</h2>
          <button className={styles.closeButton} onClick={onClose} aria-label="Close">
            <X size={24} />
          </button>
        </header>
        <div className={styles.fullPageModalBody}>
          <div className={styles.leftPanel} ref={leftPanelRef}>
            <h3>PDF Fields</h3>
            <div className={styles.fieldList}>
              {pdfFields.map(field => (
                <div
                  key={field.name}
                  data-field={field.name}
                  className={
                    styles.fieldListItem +
                    (selectedField === field.name ? ' ' + styles.fieldListItemSelected : '')
                  }
                  onClick={() => setSelectedField(field.name)}
                >
                  <div className={styles.fieldName}>{field.name}</div>
                  <input
                    className={styles.renameInput}
                    placeholder="Rename (optional)"
                    value={renameMap[field.name] || ''}
                    onChange={e => setRenameMap(rm => ({ ...rm, [field.name]: e.target.value }))}
                  />
                  <input
                    className={styles.defaultValueInput}
                    placeholder="Default value (optional)"
                    value={defaultValueMap[renameMap[field.name] || field.name] || ''}
                    onChange={e => setDefaultValueMap(dvm => ({ ...dvm, [renameMap[field.name] || field.name]: e.target.value }))}
                  />
                </div>
              ))}
            </div>
          </div>
          <div className={styles.rightPanel}>
            <div className={styles.pdfViewerContainer}>
              {pdfUrl ? (
                  <PDFViewer
                    pdfUrl={pdfUrl}
                    fields={pdfFields}
                    onFieldsExtracted={setPdfFields}
                    onFieldClick={setSelectedField}
                    highlightedFields={selectedField ? [selectedField] : []}
                    selectedFields={selectedField ? [selectedField] : []}
                  />
              ) : (
                <div className={styles.pdfViewerPlaceholder}>
                  <p>PDF Viewer</p>
                  {selectedField && <div>Highlight: {selectedField}</div>}
                </div>
              )}
            </div>
          </div>
        </div>
        <footer className={styles.fullPageModalFooter}>
          <button 
            className={styles.saveButton} 
            onClick={handleSave}
            disabled={isSaving}
          >
            <Save size={20} style={{ marginRight: 8 }} /> 
            {isSaving ? 'Saving...' : 'Save Configuration'}
          </button>
        </footer>
      </div>
    </div>
  );
}; 